/**
 * @fileoverview Componente Organism para visualización completa de mensajes de imagen en la bandeja de entrada
 * @module ImageMessageMC
 * @category Component - Organism
 * @since 1.0.0
 * @version 3.1.0
 */
import React, { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import MessageStatusMC from './MessageStatusMC';
import GroupSenderMC from './GroupSenderMC';
import CaptionMC from './CaptionMC';
import { formatText } from '../../../../util/textUtil';
import ImageMC from '../../../components/ImageMC';

/**
 * Componente Organism para visualización completa de mensajes de imagen en la bandeja de entrada.
 *
 * Encapsula la representación visual completa de un mensaje de imagen, incluyendo
 * información del remitente, imagen optimizada, caption formateado y estado del mensaje
 * en un layout responsivo con manejo de errores y estilos condicionales.
 *
 * @component ImageMessageMC
 * @category Component - Organism
 *
 * @description
 * **Características:**
 * - Composición completa de mensaje de imagen con todos sus elementos
 * - Información de remitente para mensajes grupales (GroupSenderMC)
 * - Visualización optimizada de imagen con click handler (ImageMC)
 * - Caption formateado con soporte de texto enriquecido (CaptionMC)
 * - Estado y metadatos superpuestos en la imagen (MessageStatusMC)
 * - Layout responsivo con dimensiones fijas y bordes condicionales
 * - Soporte multi-plataforma automático
 *
 * **Atomic Design:**
 * - **Nivel**: Organism
 * - **Composición**: Combina GroupSenderMC, ImageMC, MessageStatusMC, CaptionMC
 * - **Dependencias**: GroupSenderMC (molecule), ImageMC (atom), MessageStatusMC (molecule), CaptionMC (molecule)
 * - **Contexto**: Específico para visualización de mensajes de imagen en inbox
 */
// ImageMessageMC - Organismo puro, toda la lógica de UI en el hook
const ImageMessageMC = memo(({ conversation, message, onImageClick }) => {
  const { t } = useTranslation('InboxMC');
  // Memoizar caption
  const caption = useMemo(
    () => formatText(message?.contentMediaCaption || ''),
    [message?.contentMediaCaption]
  );

  // Clases del contenedor optimizadas
  const containerClasses = useMemo(() => {
    let base = 'tw-relative !tw-h-[300px] tw-border-[5px] tw-rounded-[14px]';
    if (!message?.showError) {
      base += ' tw-border-[#E4E4E4]';
    }
    return base;
  }, [message?.showError]);

  // Estilo inline para el borde en error
  const containerStyle = useMemo(() => (
    message?.showError ? { borderColor: 'transparent' } : {}
  ), [message?.showError]);

  // Handler de click en imagen
  const onImageClickFinal = useCallback(() => {
    if (typeof onImageClick === 'function') {
      onImageClick(message.id || message.identifier);
    }
  }, [onImageClick, message.id, message.identifier]);

  return (
    <>
      <GroupSenderMC message={message} />
      <div className={containerClasses} style={containerStyle}>
        <ImageMC
          platform={conversation.platform}
          data={message}
          onClick={onImageClickFinal}
          alt={t('image.alt')}
          className="tw-block tw-w-full tw-h-full tw-object-cover tw-rounded-[9px] tw-bg-white tw-cursor-pointer !tw-max-w-none !tw-max-h-none"
        />
        <MessageStatusMC isMedia message={message} platform={conversation.platform} containerClassName="!tw-text-white" />
      </div>
      <CaptionMC caption={caption} />
    </>
  );
});

ImageMessageMC.displayName = 'ImageMessageMC';

export default ImageMessageMC;
